import React, { createContext, useMemo, useContext, type ReactNode } from 'react';

export type LinkResolverOptions = {
	sortBy?: string;
	isDescending?: boolean;
	filter?: string;
	hideDone?: boolean;
	openCreateIssueDialog?: boolean;
};

export type LinksResolver = (options: LinkResolverOptions) => string;
export type LinksTarget = '_self' | '_blank';

type RequiredProps = {
	linksResolver: LinksResolver;
};

type OptionalProps = {
	linksTarget: LinksTarget;
};

type ContextProps = RequiredProps &
	Partial<OptionalProps> & {
		children: ReactNode;
	};

type FeaturesContext = RequiredProps & OptionalProps;

const defaultFeaturesContext: OptionalProps = {
	linksTarget: '_self',
};

const SummaryFeaturesContext = createContext<FeaturesContext | null>(null);

export const useSummaryFeatures = () => {
	const summaryFeaturesContext = useContext(SummaryFeaturesContext);

	if (!summaryFeaturesContext) {
		throw new Error('useSummaryFeatures must be used within a SummaryFeaturesProvider');
	}

	return summaryFeaturesContext;
};

export const SummaryFeaturesProvider = ({ children, ...props }: ContextProps) => {
	const value = useMemo(() => ({ ...defaultFeaturesContext, ...props }), [props]);

	return (
		<SummaryFeaturesContext.Provider value={value}>{children}</SummaryFeaturesContext.Provider>
	);
};
